import { useEffect, useState } from 'react';
import { api, apiFetch } from '../api/client';
import type { ApiError } from '../api/client';
import { Alert } from '../components/Field';

interface Certificate {
  certificateNo: string;
  title: string;
  learnerName: string;
  completionDate: string;
  issuedAt: string;
  status: string;
  downloadUrl?: string;
  verifyUrl?: string;
  reissued?: boolean;
  revokeReason?: string | null;
}

function day(value: string): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
}

/**
 * The learner's certificate wallet.
 *
 * This was a placeholder saying it "arrives in Milestone 7" long after that
 * milestone shipped — the API, the PDF and the public verifier all worked, and
 * the only missing piece was a page to list them on.
 *
 * The PDF is fetched rather than linked because the endpoint needs an
 * Authorization header, which a plain anchor cannot send. The verification
 * link is the opposite: deliberately public, so it can be given to an employer.
 */
export function Certificates() {
  const [items, setItems] = useState<Certificate[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<{ items: Certificate[] }>('/my/certificates')
      .then((result) => {
        if (!cancelled) setItems(result.items);
      })
      .catch((caught) => {
        if (!cancelled) setError(caught as ApiError);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function download(certificate: Certificate) {
    setBusy(certificate.certificateNo);
    try {
      const response = await apiFetch(`/certificates/${certificate.certificateNo}/pdf`);
      if (!response.ok) throw new Error(`The certificate could not be downloaded (${response.status}).`);

      // Handed to the browser as a blob so the filename is ours and the
      // request can carry the token.
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${certificate.certificateNo}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (caught) {
      setError({ message: (caught as Error).message } as ApiError);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">Certificates</h1>
        <p className="page__lede">
          Everything you have earned. Each one carries a number anyone can check without an
          account.
        </p>
      </div>

      {error ? <Alert kind="error" title={error.message} /> : null}

      {!items ? (
        <div className="card">
          <div className="empty">Loading…</div>
        </div>
      ) : items.length === 0 ? (
        <div className="card">
          <div className="empty">
            No certificates yet.
            <br />
            <span className="muted">
              Finish a course — and pass its assessment where one is required — and it appears
              here.
            </span>
          </div>
        </div>
      ) : (
        <div className="certgrid">
          {items.map((certificate) => (
            <article className="certcard" key={certificate.certificateNo}>
              <div className="certcard__seal" aria-hidden="true">
                ✦
              </div>
              <h2 className="certcard__title">{certificate.title}</h2>
              <dl className="certcard__meta">
                <div>
                  <dt>Certificate</dt>
                  <dd className="certcard__no">{certificate.certificateNo}</dd>
                </div>
                <div>
                  <dt>Completed</dt>
                  <dd>{day(certificate.completionDate)}</dd>
                </div>
                <div>
                  <dt>Issued</dt>
                  <dd>{day(certificate.issuedAt)}</dd>
                </div>
              </dl>

              {certificate.status !== 'ISSUED' ? (
                <p className="certcard__revoked">
                  {certificate.status === 'REVOKED' ? 'Revoked' : certificate.status}
                  {certificate.revokeReason ? ` — ${certificate.revokeReason}` : ''}
                </p>
              ) : null}

              <div className="certcard__actions">
                <button
                  className="btn btn--sm"
                  onClick={() => void download(certificate)}
                  disabled={busy === certificate.certificateNo}
                >
                  {busy === certificate.certificateNo ? 'Preparing…' : 'Download PDF'}
                </button>
                <a
                  className="btn btn--sm btn--ghost"
                  href={`/verify/${certificate.certificateNo}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Verify publicly
                </a>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}
